import React from 'react';
import { ReactComponent as DownloadIcon } from '../../images/downloadIcon.svg';
import { ReactComponent as SettingsIcon } from '../../images/settingsIcon.svg';
import { ReactComponent as WarningIcon } from '../../images/warningIcon.svg';
import { ReactComponent as ClosedLock } from '../../images/closedLock.svg';
import { ReactComponent as OpenedLock } from '../../images/openedLock.svg';
import { ReactComponent as MagnetIcon } from '../../images/magnetIcon.svg';
import { ReactComponent as MagnetOffIcon } from '../../images/magnetOffIcon.svg';
import Tooltip from '../Tooltip';
import CursorModeSelector from './CursorModeSelector';
import { TOP_BAR_HEIGHT } from '../../utils/constants';

/**
 * Barre supérieure de l'interface StageView
 */
export default function TopBar({
  viewMode,
  toggleViewMode,
  handleDownloadPdf,
  cursorMode,
  setCursorMode,
  selectedSkillId,
  skills,
  runOptimization,
  forcedPosition,
  setForcedPosition,
  sessionErrors,
  showErrorMenu,
  setShowErrorMenu,
  onOpenGridSettings,
  snapEnabled,
  setSnapEnabled,
  // Undo/Redo
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}) {
  return (
    <div
      style={{
        width: '100%',
        height: `${TOP_BAR_HEIGHT}px`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 8px',
        gap: '8px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #d1d1d6',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 100,
      }}
    >
      {/* Partie gauche */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {/* Boutons Undo/Redo */}
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <Tooltip text="Annuler (Ctrl+Z)">
            <div
              onClick={canUndo ? onUndo : undefined}
              onMouseEnter={(e) => canUndo && (e.currentTarget.style.backgroundColor = '#ddd')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = canUndo ? '#fff' : '#f5f5f5')}
              style={{
                padding: '6px 8px',
                backgroundColor: canUndo ? '#fff' : '#f5f5f5',
                cursor: canUndo ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '9px 0 0 9px',
                border: '1px solid #ccc',
                borderRight: 'none',
                opacity: canUndo ? 1 : 0.4,
                transition: 'background-color 0.2s, opacity 0.2s',
              }}
            >
              <span style={{ fontSize: '14px' }}>↩</span>
            </div>
          </Tooltip>
          <Tooltip text="Refaire (Ctrl+Y)">
            <div
              onClick={canRedo ? onRedo : undefined}
              onMouseEnter={(e) => canRedo && (e.currentTarget.style.backgroundColor = '#ddd')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = canRedo ? '#fff' : '#f5f5f5')}
              style={{
                padding: '6px 8px',
                backgroundColor: canRedo ? '#fff' : '#f5f5f5',
                cursor: canRedo ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '0 9px 9px 0',
                border: '1px solid #ccc',
                opacity: canRedo ? 1 : 0.4,
                transition: 'background-color 0.2s, opacity 0.2s',
              }}
            >
              <span style={{ fontSize: '14px' }}>↪</span>
            </div>
          </Tooltip>
        </div>

        {/* Bouton Télécharger */}
        <Tooltip text="Exporter la scène en PDF">
          <div
            onClick={handleDownloadPdf}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ddd'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
            style={{
              padding: '6px 8px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              borderRight: '1px solid #ccc',
              left: '50%',
              top: '50%',
              userSelect: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '9px',
              border: '1px solid #ccc',
              transition: 'box-shadow 0.2s, background-color 0.6s',
            }}
          >
            <DownloadIcon style={{ width: '16px', height: '16px', fill: '#7A7A7A' }} />
          </div>
        </Tooltip>

        {/* Bouton Vue Trajectoire */}
        <Tooltip text="Afficher les trajectoires individuelles des danseurs">
          <button
            onClick={toggleViewMode}
            style={{
              padding: '8px 12px',
              borderRadius: '9px',
              border: 'none',
              backgroundColor: viewMode === 'scene' ? '#007bff' : '#0063cd',
              color: '#fff',
              cursor: 'pointer',
              height: '32px',
              boxShadow:
                viewMode === 'scene'
                  ? 'none'
                  : 'inset 2px 2px 5px rgba(0, 0, 0, 0.3)',
              transition: 'box-shadow 0.2s, background-color 0.2s',
            }}
          >
            Vue Trajectoire
          </button>
        </Tooltip>

        {/* Bouton Paramètres de grille */}
        <Tooltip text="Paramètres de la grille (colonnes/lignes)">
          <div
            onClick={onOpenGridSettings}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ddd'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
            style={{
              padding: '6px 8px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '9px',
              border: '1px solid #ccc',
              transition: 'box-shadow 0.2s, background-color 0.6s',
            }}
          >
            <SettingsIcon style={{ width: '16px', height: '16px', fill: '#7A7A7A' }} />
          </div>
        </Tooltip>

        {/* Bouton Aimant (snap à la grille) */}
        <Tooltip text={snapEnabled ? "Désactiver l'aimantage à la grille" : "Activer l'aimantage à la grille"}>
          <div
            onClick={() => setSnapEnabled(!snapEnabled)}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = snapEnabled ? '#ddd' : '#ddd'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = snapEnabled ? '#ecececff' : '#fff'}
            style={{
              padding: '6px 8px',
              backgroundColor: snapEnabled ? '#ddd' : '#fff',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '9px',
              border: '1px solid #ccc',
              transition: 'box-shadow 0.2s, background-color 0.2s, border-color 0.2s',
              boxShadow: snapEnabled ? 'inset 1px 1px 3px rgba(0, 0, 0, 0.15)' : 'none',
            }}
          >
            {snapEnabled ? (
              <MagnetIcon style={{ width: '16px', height: '16px' }} />
            ) : (
              <MagnetOffIcon style={{ width: '16px', height: '16px' }} />
            )}
          </div>
        </Tooltip>
      </div>

      {/* Sélecteur de curseur, centré */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <CursorModeSelector 
          cursorMode={cursorMode}
          setCursorMode={setCursorMode}
          selectedSkillId={selectedSkillId}
          skills={skills}
        />
      </div>

      {/* Partie droite */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            display: 'flex',
            borderRadius: '9px',
            overflow: 'hidden',
            backgroundColor: 'transparent',
            height: '32px',
          }}
        >
          {/* Bouton d'optimisation avec infobox */}
          <Tooltip text="Lancer l'optimisation pour assigner automatiquement les danseurs aux positions, en minimisant les déplacements et en respectant les contraintes de skills">
            <button
              onClick={runOptimization}
              style={{
                padding: '6px 10px',
                borderLeft: '9px',
                border: 'none',
                backgroundColor: '#34c759',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Lancer l'optimisation
            </button>
          </Tooltip>

          {/* Bouton cadenas avec infobox */}
          <Tooltip text="Le cadenas permet de conserver les positions déjà assignées lors de l'optimisation">
            <button
              onClick={() => setForcedPosition(!forcedPosition)}
              style={{
                marginLeft: '0px',
                padding: '4px',
                paddingRight: '5px',
                border: '1px solid #ccc',
                borderTopRightRadius: '9px',
                borderBottomRightRadius: '9px',
                borderLeft: 'none',
                cursor: 'pointer',
                borderRight: '9px',
                display: 'flex',
                alignItems: 'center',
                width: '30px',
                justifyContent: 'center',
              }}
            >
              {forcedPosition ? (
                <ClosedLock style={{ width: '16px', height: '16px' }} />
              ) : (
                <OpenedLock style={{ width: '16px', height: '16px' }} />
              )}
            </button>
          </Tooltip>

          {/* Bouton alertes */}
          <button
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                sessionErrors.length > 0 ? '#ffb731ff' : '#ddd')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                sessionErrors.length > 0 ? '#ffce72ff' : '#fff')
            }
            onClick={() => {
              setShowErrorMenu((prev) => !prev);
            }}
            style={{
              padding: '6px 8px',
              backgroundColor: sessionErrors.length > 0 ? '#ffce72ff' : '#fff',
              cursor: 'pointer',
              borderRight: '1px solid #ccc',
              left: '50%',
              top: '50%',
              userSelect: 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '9px',
              border: '1px solid #ccc',
              transition: 'box-shadow 0.2s, background-color 0.2s',
              marginLeft: '6px',
            }}
            title="Voir les alertes"
          >
            <WarningIcon style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>
    </div>
  );
}
